import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box, Typography, IconButton, TableCell, TableRow, TableHead,
  TableSortLabel, Popover, List, ListItem, ListItemText, Checkbox,
  TextField, InputAdornment, Button, Badge, alpha, Tooltip
} from '@mui/material';
import { FilterList, Clear, Search, ViewColumn } from '@mui/icons-material';

/**
 * useTableSort — reusable hook for table sort + filter across the app.
 *
 * Usage:
 *   const columns = [
 *     { id: 'name', label: 'Name', getValue: r => r.name },
 *     { id: 'amount', label: 'Amount', getValue: r => parseFloat(r.amount) || 0, align: 'right', filterable: false },
 *   ];
 *   const { sortedData, renderHeader, renderFilterPopover, activeFilterCount, clearAllFilters } = useTableSort(columns, rows);
 *
 * All columns are sortable and filterable by default. Set sortable: false or filterable: false to disable.
 * Provide getDisplay for custom display text used in filter options (defaults to String(getValue(r))).
 */
export function useTableSort(columns, data, { defaultSort = null, defaultDir = 'asc', storageKey = null } = {}) {
  const [sortField, setSortField] = useState(defaultSort || (columns[0]?.id ?? null));
  const [sortDir, setSortDir] = useState(defaultDir);
  const [filters, setFilters] = useState({});
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterCol, setFilterCol] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');

  // Column visibility
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    if (!storageKey) return new Set();
    try {
      const saved = localStorage.getItem(`tableColumns_${storageKey}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch (e) { return new Set(); }
  });
  const [colToggleAnchor, setColToggleAnchor] = useState(null);

  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(`tableColumns_${storageKey}`, JSON.stringify([...hiddenColumns]));
    }
  }, [hiddenColumns, storageKey]);

  const visibleColumns = useMemo(() => {
    if (!storageKey) return columns;
    return columns.filter((col, idx) => idx === 0 || !hiddenColumns.has(col.id));
  }, [columns, hiddenColumns, storageKey]);

  const isColumnVisible = useCallback((colId) => {
    if (!storageKey) return true;
    return !hiddenColumns.has(colId);
  }, [hiddenColumns, storageKey]);

  const toggleColumnVisibility = useCallback((colId) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId);
      else next.add(colId);
      return next;
    });
  }, []);

  const showAllColumns = useCallback(() => setHiddenColumns(new Set()), []);

  const handleSort = useCallback((field) => {
    setSortDir(prev => (sortField === field && prev === 'asc' ? 'desc' : 'asc'));
    setSortField(field);
  }, [sortField]);

  const toggleFilter = useCallback((colId, value) => {
    setFilters(f => {
      const cur = f[colId] || [];
      const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value];
      const updated = { ...f };
      if (next.length === 0) delete updated[colId]; else updated[colId] = next;
      return updated;
    });
  }, []);

  const clearFilter = useCallback((colId) => {
    setFilters(f => { const u = { ...f }; delete u[colId]; return u; });
  }, []);

  const clearAllFilters = useCallback(() => setFilters({}), []);

  const activeFilterCount = Object.keys(filters).length;

  // Build filter options per filterable column
  const filterOptions = useMemo(() => {
    const opts = {};
    columns.filter(c => c.filterable !== false).forEach(col => {
      const vals = new Set();
      data.forEach(r => {
        const v = col.getDisplay ? col.getDisplay(r) : String(col.getValue(r));
        if (v && v !== '—' && v !== 'undefined' && v !== 'null' && v !== '') vals.add(v);
      });
      opts[col.id] = [...vals].sort();
    });
    return opts;
  }, [data, columns]);

  // Filter + sort data
  const sortedData = useMemo(() => {
    let result = data.filter(r => {
      for (const [colId, vals] of Object.entries(filters)) {
        const col = columns.find(c => c.id === colId);
        if (!col) continue;
        const rv = col.getDisplay ? col.getDisplay(r) : String(col.getValue(r));
        if (!vals.includes(rv)) return false;
      }
      return true;
    });
    if (sortField) {
      const col = columns.find(c => c.id === sortField);
      if (col && col.sortable !== false) {
        result = [...result].sort((a, b) => {
          let av = col.getValue(a), bv = col.getValue(b);
          if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
          if (av < bv) return sortDir === 'asc' ? -1 : 1;
          if (av > bv) return sortDir === 'asc' ? 1 : -1;
          return 0;
        });
      }
    }
    return result;
  }, [data, filters, sortField, sortDir, columns]);

  const openFilter = useCallback((e, colId) => {
    setFilterAnchor(e.currentTarget);
    setFilterCol(colId);
    setFilterSearch('');
  }, []);

  const closeFilter = useCallback(() => {
    setFilterAnchor(null);
    setFilterCol(null);
    setFilterSearch('');
  }, []);

  // Render table header row
  const renderHeader = useCallback((extraCells = null) => (
    <TableHead>
      <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#f8fafc', borderBottom: '2px solid #e2e8f0' } }}>
        {visibleColumns.map(col => (
          <TableCell key={col.id} align={col.align || 'left'} sx={{ whiteSpace: 'nowrap', ...(col.headerSx || {}) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
              {col.sortable !== false ? (
                <TableSortLabel active={sortField === col.id} direction={sortField === col.id ? sortDir : 'asc'}
                  onClick={() => handleSort(col.id)}
                  sx={{ '& .MuiTableSortLabel-icon': { fontSize: 16, opacity: sortField === col.id ? 1 : 0.4 }, fontWeight: 700, fontSize: '0.8rem' }}>
                  {col.label}
                </TableSortLabel>
              ) : (
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{col.label}</Typography>
              )}
              {col.filterable !== false && (
                <IconButton size="small" onClick={e => openFilter(e, col.id)}
                  sx={{ ml: 0.2, p: 0.3, color: filters[col.id] ? '#f59e0b' : '#94a3b8', '&:hover': { color: '#f59e0b' } }}>
                  <Badge variant="dot" invisible={!filters[col.id]} color="warning">
                    <FilterList sx={{ fontSize: 15 }} />
                  </Badge>
                </IconButton>
              )}
            </Box>
          </TableCell>
        ))}
        {extraCells}
      </TableRow>
    </TableHead>
  ), [visibleColumns, sortField, sortDir, filters, handleSort, openFilter]);

  // Render filter popover
  const renderFilterPopover = useCallback(() => {
    const opts = filterCol ? (filterOptions[filterCol] || []) : [];
    const filtered = filterSearch ? opts.filter(o => o.toLowerCase().includes(filterSearch.toLowerCase())) : opts;
    return (
      <Popover open={Boolean(filterAnchor)} anchorEl={filterAnchor} onClose={closeFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 220, maxHeight: 380 } }}>
        {filterCol && (
          <Box>
            <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155' }}>
                Filter: {columns.find(c => c.id === filterCol)?.label}
              </Typography>
              {filters[filterCol] && (
                <Button size="small" onClick={() => clearFilter(filterCol)}
                  sx={{ fontSize: '0.68rem', color: '#ef4444', minWidth: 0, textTransform: 'none', fontWeight: 600, p: 0 }}>Clear</Button>
              )}
            </Box>
            <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
              <TextField size="small" fullWidth placeholder="Search..." value={filterSearch}
                onChange={e => setFilterSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
                  sx: { fontSize: '0.78rem', height: 32, borderRadius: 2, bgcolor: '#f8fafc' }
                }}
                sx={{ '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#e2e8f0' } } }} />
            </Box>
            <List dense sx={{ py: 0.5, maxHeight: 240, overflow: 'auto' }}>
              {filtered.map(opt => (
                <ListItem key={opt} dense button onClick={() => toggleFilter(filterCol, opt)}
                  sx={{ py: 0.2, px: 1.5, '&:hover': { bgcolor: alpha('#f59e0b', 0.06) } }}>
                  <Checkbox size="small" checked={(filters[filterCol] || []).includes(opt)}
                    sx={{ p: 0.3, mr: 1, color: '#cbd5e1', '&.Mui-checked': { color: '#f59e0b' } }} />
                  <ListItemText primary={opt} primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: 500 }} />
                </ListItem>
              ))}
              {filtered.length === 0 && (
                <ListItem><ListItemText primary="No matches" primaryTypographyProps={{ fontSize: '0.78rem', color: '#94a3b8', fontStyle: 'italic' }} /></ListItem>
              )}
            </List>
          </Box>
        )}
      </Popover>
    );
  }, [filterAnchor, filterCol, filterOptions, filters, filterSearch, columns, closeFilter, clearFilter, toggleFilter]);

  // Render "Clear X filters" button for toolbar
  const renderClearFilters = useCallback(() => {
    if (activeFilterCount === 0) return null;
    return (
      <Button size="small" startIcon={<Clear sx={{ fontSize: 16 }} />} onClick={clearAllFilters}
        sx={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none' }}>
        Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
      </Button>
    );
  }, [activeFilterCount, clearAllFilters]);

  // Render column visibility toggle for toolbar
  const renderColumnToggle = useCallback(() => {
    if (!storageKey) return null;
    return (
      <>
        <Tooltip title="Show/hide columns" arrow>
          <IconButton size="small" onClick={e => setColToggleAnchor(e.currentTarget)}
            sx={{ color: hiddenColumns.size > 0 ? '#f59e0b' : '#64748b', border: '1px solid',
              borderColor: hiddenColumns.size > 0 ? alpha('#f59e0b', 0.3) : '#e2e8f0',
              borderRadius: 2, p: 0.5, '&:hover': { borderColor: '#f59e0b', color: '#f59e0b' } }}>
            <Badge badgeContent={hiddenColumns.size || 0} color="warning" max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 16, height: 16 } }}>
              <ViewColumn sx={{ fontSize: 20 }} />
            </Badge>
          </IconButton>
        </Tooltip>
        <Popover open={Boolean(colToggleAnchor)} anchorEl={colToggleAnchor}
          onClose={() => setColToggleAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', minWidth: 200, maxHeight: 400 } }}>
          <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#334155' }}>Toggle Columns</Typography>
            {hiddenColumns.size > 0 && (
              <Button size="small" onClick={showAllColumns}
                sx={{ fontSize: '0.7rem', color: '#3b82f6', minWidth: 0, textTransform: 'none', fontWeight: 600, p: 0 }}>Show All</Button>
            )}
          </Box>
          <List dense sx={{ py: 0.5 }}>
            {columns.map((col, idx) => (
              <ListItem key={col.id} dense button
                disabled={idx === 0}
                onClick={() => idx > 0 && toggleColumnVisibility(col.id)}
                sx={{ py: 0.3, px: 1.5, '&:hover': { bgcolor: alpha('#f59e0b', 0.06) } }}>
                <Checkbox size="small"
                  checked={idx === 0 || !hiddenColumns.has(col.id)}
                  disabled={idx === 0}
                  sx={{ p: 0.3, mr: 1, color: '#cbd5e1', '&.Mui-checked': { color: '#f59e0b' } }} />
                <ListItemText primary={col.label}
                  primaryTypographyProps={{ fontSize: '0.8rem', fontWeight: 500, color: idx === 0 ? '#94a3b8' : '#334155' }} />
              </ListItem>
            ))}
          </List>
        </Popover>
      </>
    );
  }, [storageKey, columns, hiddenColumns, colToggleAnchor, toggleColumnVisibility, showAllColumns]);

  return {
    sortedData,
    renderHeader,
    renderFilterPopover,
    renderClearFilters,
    renderColumnToggle,
    activeFilterCount,
    clearAllFilters,
    sortField,
    sortDir,
    filters,
    visibleColumns,
    isColumnVisible,
  };
}
